/**
 * useGameSession Hook
 * Create, manage, and track game sessions
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  GameSession,
  CreateSessionParams,
  UpdateSessionParams,
  SessionParticipant,
  GameResult,
} from "../types";

interface UseGameSessionReturn {
  /** Current session data */
  session: GameSession | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Create a new game session */
  createSession: (params: CreateSessionParams) => Promise<GameSession | null>;
  /** Load an existing session by ID */
  loadSession: (sessionId: string) => Promise<void>;
  /** Update session data */
  updateSession: (params: UpdateSessionParams) => Promise<void>;
  /** Start the game (change status to active) */
  startGame: () => Promise<void>;
  /** Complete the game with results */
  completeGame: (result: GameResult, pointsAwarded?: number) => Promise<void>;
  /** Cancel the game */
  cancelGame: () => Promise<void>;
  /** Add a participant to the session */
  addParticipant: (participant: Omit<SessionParticipant, "joined_at">) => Promise<void>;
  /** Remove a participant from the session */
  removeParticipant: (userId: string) => Promise<void>;
  /** Update game state */
  updateGameState: (state: Record<string, unknown>) => Promise<void>;
  /** Update participant score */
  updateParticipantScore: (userId: string, score: number) => Promise<void>;
  /** Refetch session data */
  refetch: () => Promise<void>;
  /** Clear session */
  clearSession: () => void;
}

/**
 * Hook to manage a single game session
 */
export const useGameSession = (
  initialSessionId?: string
): UseGameSessionReturn => {
  const { user, activeProfile, activeWeekend, userTeam } = useAuth();

  const [session, setSession] = useState<GameSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  /**
   * Subscribe to session changes via Realtime
   */
  const subscribeToSession = useCallback((sessionId: string) => {
    // Unsubscribe from previous session
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
    }

    subscriptionRef.current = supabase
      .channel(`game_session:${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "game_sessions",
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          if (payload.eventType === "UPDATE") {
            setSession(payload.new as GameSession);
          } else if (payload.eventType === "DELETE") {
            setSession(null);
          }
        }
      )
      .subscribe();
  }, []);

  /**
   * Cleanup subscription on unmount
   */
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, []);

  /**
   * Load session from database
   */
  const loadSession = useCallback(
    async (sessionId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from("game_sessions")
          .select(
            `
            *,
            game:game_definitions(*),
            creator:profiles!game_sessions_created_by_fkey(id, full_name, avatar_url)
          `
          )
          .eq("id", sessionId)
          .single();

        if (fetchError) {
          throw new Error(fetchError.message);
        }

        setSession(data as GameSession);
        subscribeToSession(sessionId);
      } catch (err) {
        console.error("Error loading session:", err);
        setError(err instanceof Error ? err : new Error("Failed to load session"));
      } finally {
        setIsLoading(false);
      }
    },
    [subscribeToSession]
  );

  // Load initial session if provided
  useEffect(() => {
    if (initialSessionId) {
      loadSession(initialSessionId);
    }
  }, [initialSessionId, loadSession]);

  /**
   * Create a new game session
   */
  const createSession = useCallback(
    async (params: CreateSessionParams): Promise<GameSession | null> => {
      if (!activeProfile?.id) {
        setError(new Error("User not authenticated"));
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Build initial participant (the creator)
        const creatorParticipant: SessionParticipant = {
          user_id: activeProfile.id,
          team_id: userTeam?.id,
          display_name: activeProfile.full_name || "Unknown",
          avatar_url: activeProfile.avatar_url || undefined,
          role: "host",
          score: 0,
          joined_at: new Date().toISOString(),
        };

        const allParticipants = [
          creatorParticipant,
          ...(params.participants || []).map((p) => ({
            ...p,
            joined_at: new Date().toISOString(),
          })),
        ];

        const sessionData = {
          game_slug: params.game_slug,
          weekend_id: params.mode === "weekend" ? (params.weekend_id || activeWeekend?.id) : null,
          mode: params.mode,
          session_type: params.session_type,
          status: "waiting" as const,
          created_by: activeProfile.id,
          participants: allParticipants,
          game_state: params.initial_state || {},
          quiz_id: params.quiz_id || null,
        };

        const { data, error: insertError } = await supabase
          .from("game_sessions")
          .insert(sessionData)
          .select(
            `
            *,
            game:game_definitions(*),
            creator:profiles!game_sessions_created_by_fkey(id, full_name, avatar_url)
          `
          )
          .single();

        if (insertError) {
          throw new Error(insertError.message);
        }

        const newSession = data as GameSession;
        setSession(newSession);
        subscribeToSession(newSession.id);

        return newSession;
      } catch (err) {
        console.error("Error creating session:", err);
        setError(err instanceof Error ? err : new Error("Failed to create session"));
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [activeProfile, userTeam, activeWeekend, subscribeToSession]
  );

  /**
   * Update session data
   */
  const updateSession = useCallback(
    async (params: UpdateSessionParams) => {
      if (!session?.id) {
        setError(new Error("No active session"));
        return;
      }

      try {
        const updates: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
        };

        if (params.status) updates.status = params.status;
        if (params.game_state) updates.game_state = params.game_state;
        if (params.result) updates.result = params.result;
        if (params.winner_user_id) updates.winner_user_id = params.winner_user_id;
        if (params.winner_team_id) updates.winner_team_id = params.winner_team_id;
        if (params.points_awarded !== undefined) updates.points_awarded = params.points_awarded;

        // Handle participant changes
        if (params.add_participant) {
          const newParticipant = {
            ...params.add_participant,
            joined_at: new Date().toISOString(),
          };
          updates.participants = [...session.participants, newParticipant];
        }

        if (params.remove_participant_id) {
          updates.participants = session.participants.filter(
            (p) => p.user_id !== params.remove_participant_id
          );
        }

        const { error: updateError } = await supabase
          .from("game_sessions")
          .update(updates)
          .eq("id", session.id);

        if (updateError) {
          throw new Error(updateError.message);
        }

        // Optimistic update (realtime will also update)
        setSession((prev) =>
          prev ? { ...prev, ...updates } as GameSession : null
        );
      } catch (err) {
        console.error("Error updating session:", err);
        setError(err instanceof Error ? err : new Error("Failed to update session"));
      }
    },
    [session]
  );

  /**
   * Start the game
   */
  const startGame = useCallback(async () => {
    await updateSession({
      status: "active",
    });

    // Also update started_at
    if (session?.id) {
      await supabase
        .from("game_sessions")
        .update({ started_at: new Date().toISOString() })
        .eq("id", session.id);
    }
  }, [session?.id, updateSession]);

  /**
   * Complete the game
   */
  const completeGame = useCallback(
    async (result: GameResult, pointsAwarded?: number) => {
      await updateSession({
        status: "completed",
        result,
        winner_user_id: result.winner_user_id,
        winner_team_id: result.winner_team_id,
        points_awarded: pointsAwarded,
      });

      // Also update completed_at
      if (session?.id) {
        await supabase
          .from("game_sessions")
          .update({ completed_at: new Date().toISOString() })
          .eq("id", session.id);
      }

      // Update user stats for each participant
      if (session?.participants) {
        for (const participant of session.participants) {
          const isWinner = participant.user_id === result.winner_user_id;
          await supabase.rpc("update_user_game_stats", {
            p_user_id: participant.user_id,
            p_game_slug: session.game_slug,
            p_won: isWinner,
            p_points: isWinner ? pointsAwarded || 0 : 0,
            p_score: participant.score,
          });
        }
      }

      // Award points to team in weekend mode
      if (session?.mode === "weekend" && pointsAwarded && pointsAwarded > 0) {
        const participant = session.participants?.[0];
        const teamId = participant?.team_id;

        if (teamId && activeProfile?.id) {
          try {
            // Get current team score
            const { data: team } = await supabase
              .from("teams")
              .select("total_score")
              .eq("id", teamId)
              .single();

            const previousTotal = team?.total_score || 0;
            const newTotal = previousTotal + pointsAwarded;

            // Add to score_history
            await supabase.from("score_history").insert({
              team_id: teamId,
              points: pointsAwarded,
              previous_total: previousTotal,
              new_total: newTotal,
              reason: `${session.game_slug === "quiz" ? "Quiz" : "Game"} voltooid`,
              game_id: session.id,
              game_type: session.game_slug,
              awarded_by: activeProfile.id,
              awarded_to: activeProfile.id,
            });

            // Update team total_score
            await supabase
              .from("teams")
              .update({ total_score: newTotal })
              .eq("id", teamId);
          } catch (err) {
            console.error("Error awarding team points:", err);
          }
        }
      }
    },
    [session, updateSession, activeProfile?.id]
  );

  /**
   * Cancel the game
   */
  const cancelGame = useCallback(async () => {
    await updateSession({ status: "cancelled" });
  }, [updateSession]);

  /**
   * Add a participant
   */
  const addParticipant = useCallback(
    async (participant: Omit<SessionParticipant, "joined_at">) => {
      await updateSession({ add_participant: participant });
    },
    [updateSession]
  );

  /**
   * Remove a participant
   */
  const removeParticipant = useCallback(
    async (userId: string) => {
      await updateSession({ remove_participant_id: userId });
    },
    [updateSession]
  );

  /**
   * Update game state
   */
  const updateGameState = useCallback(
    async (state: Record<string, unknown>) => {
      if (!session) return;

      const newState = { ...session.game_state, ...state };
      await updateSession({ game_state: newState });
    },
    [session, updateSession]
  );

  /**
   * Update participant score
   */
  const updateParticipantScore = useCallback(
    async (userId: string, score: number) => {
      if (!session) return;

      const updatedParticipants = session.participants.map((p) =>
        p.user_id === userId ? { ...p, score } : p
      );

      const { error: updateError } = await supabase
        .from("game_sessions")
        .update({ participants: updatedParticipants })
        .eq("id", session.id);

      if (updateError) {
        console.error("Error updating participant score:", updateError);
      }
    },
    [session]
  );

  /**
   * Refetch session data
   */
  const refetch = useCallback(async () => {
    if (session?.id) {
      await loadSession(session.id);
    }
  }, [session?.id, loadSession]);

  /**
   * Clear session
   */
  const clearSession = useCallback(() => {
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
    }
    setSession(null);
    setError(null);
  }, []);

  return {
    session,
    isLoading,
    error,
    createSession,
    loadSession,
    updateSession,
    startGame,
    completeGame,
    cancelGame,
    addParticipant,
    removeParticipant,
    updateGameState,
    updateParticipantScore,
    refetch,
    clearSession,
  };
};

